const formFields = require('../formFields/formFields');
var gtm = require('googletagmanager/tagManager');

/**
* Calculates the width of the browser scrollbar.
*
* @returns {number} The width of the scrollbar in pixels.
*/
function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
}

/**
* Removes focus trap event listeners from the modal.
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal element.
*/
function removeFocusTrap($trayPopup) {
    const handler = $trayPopup.data('focusTrapHandler');
    if (handler) {
        $(document).off('keydown', handler);
        $trayPopup.removeData('focusTrapHandler');
    }
}

/**
* Keeps trigger expanded state synchronized with tray visibility when supported.
*
* @param {HTMLElement} triggerElement - The element used to open the tray.
* @param {boolean} isExpanded - Whether the tray is expanded/open.
*/
function setTriggerExpandedState(triggerElement, isExpanded) {
    if (!triggerElement || !triggerElement.setAttribute || !triggerElement.getAttribute) {
        return;
    }

    if (triggerElement.getAttribute('aria-expanded') !== null) {
        triggerElement.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }
}

/**
* Creates the tray modal and backdrop if they don't exist.
*
* @param {string} trayId - Unique tray ID (string or number).
* @param {string} customClassName - Optional custom class for tray modal.
* @param {string} ariaLabel - Optional accessible label for the dialog.
* @returns {JQuery<HTMLElement>} The jQuery object of the tray modal.
*/
function getOrCreateTrayPopup(trayId, customClassName = '', ariaLabel = '') {
    const traySelector = `#tray-modal-${trayId}`;
    let $trayPopup = $(traySelector);

    if (!$trayPopup.length) {
        const $backdrop = $(`<div class="tray-modal-backdrop tray-modal-backdrop-${trayId}"></div>`);
        var labelAttr = ariaLabel ? ` aria-label="${ariaLabel}"` : '';
        $trayPopup = $(`
            <div id="tray-modal-${trayId}" class="tray-modal ${customClassName}" role="dialog" aria-modal="true"${labelAttr}>
                <div class="tray-modal-content"></div>
            </div>
        `);
        $('body').append($backdrop, $trayPopup);
    } else if (ariaLabel && !$trayPopup.attr('aria-label')) {
        $trayPopup.attr('aria-label', ariaLabel);
    }

    return $trayPopup;
}

/**
* Sets an accessible name for the tray modal.
* Uses an in-tray title first, then falls back to the trigger label/text.
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal element.
* @param {HTMLElement} triggerElement - The element that opened the tray modal.
*/
function setTrayAccessibleName($trayPopup, triggerElement) {
    const trayPopupId = $trayPopup.attr('id');
    const $trayTitle = ($trayPopup.find('.mk-mini-cart__title').length > 0) ? $trayPopup.find('.mk-mini-cart__title').first() : $trayPopup.find('.js-tray-modal-title').first();

    if ($trayTitle.length > 0 && $trayTitle.text().trim().length > 0) {
        if (!$trayTitle.attr('id')) {
            $trayTitle.attr('id', `${trayPopupId}-title`);
        }

        $trayPopup.attr('aria-labelledby', $trayTitle.attr('id'));
        $trayPopup.removeAttr('aria-label');
        return;
    }

    const triggerLabel = triggerElement.getAttribute('aria-label');
    const triggerText = $(triggerElement).text().replace(/\s+/g, ' ').trim();
    const fallbackLabel = triggerLabel || triggerText || 'Dialog';

    $trayPopup.removeAttr('aria-labelledby');
    $trayPopup.attr('aria-label', fallbackLabel);
}

/**
* Displays the tray modal with a slight delay and handles body scroll lock.
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal DOM element.
*/
function showPopupWithDelay($trayPopup) {
    $trayPopup.addClass('tray-popup-visible');
    const trayId = $trayPopup.attr('id').replace('tray-modal-', '');
    $(`.tray-modal-backdrop-${trayId}`).addClass('tray-popup-visible');
    const scrollbarWidth = getScrollbarWidth();
    document.documentElement.style.setProperty('--tray-popup-visible', `${scrollbarWidth + 15}px`);
    let $trayPopupContent = $trayPopup.find('.js-custom-tray-content:not(.d-none)');

    if ($trayPopupContent.length > 0) {
        $('body').trigger('tray:adjustHeight', [$trayPopupContent]);
    } else {
        $('body').trigger('tray:adjustHeight', [$trayPopup]);
    }

    $('body').addClass('modal-open');
}

/**
* Closes the tray modal with transition and removes it from the DOM.
* Also restores focus to the element that opened the modal.
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal element.
*/
function closePopup($trayPopup) {
    const trayId = $trayPopup.attr('id').replace('tray-modal-', '');
    $trayPopup.removeClass('tray-popup-visible');
    $(`.tray-modal-backdrop-${trayId}`).removeClass('tray-popup-visible');

    // Remove focus trap
    removeFocusTrap($trayPopup);

    // Restore focus to the element that opened the modal
    const previousFocus = $trayPopup.data('previousFocus');
    setTriggerExpandedState(previousFocus, false);

    if (previousFocus && previousFocus.focus && document.contains(previousFocus)) {
        setTimeout(() => {
            previousFocus.focus();
        }, 150);
    }

    // Clear the stored focus reference to prevent memory leaks
    $trayPopup.removeData('previousFocus');

    // A360SFCC-3382 ensure we don't keep old bind to stop multiple 'closePopup' calls
    $('body').off('click touchstart', `.tray-modal-backdrop-${trayId}`);

    setTimeout(() => {
        const $modals = $('.modal.show');
        if ($modals.length === 0 && $('body').find('.tray-popup-visible:not(".tray-modal-backdrop")').length === 0 ) {
            $('body').removeClass('modal-open');
        }

        if (!$trayPopup.hasClass('tray-popup-visible')) {
            $(`.tray-modal-backdrop-${trayId}`).remove();
            $trayPopup.remove();
        }
        $('body').trigger('tray:closed', $trayPopup);
    }, 100);
}

/**
* Sets up focus trap within the modal for accessibility.
* Ensures tab navigation cycles within the modal (from last to first and vice versa).
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal DOM element.
*/
function setupFocusTrap($trayPopup) {
    const focusableSelector = 'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

    const handleKeyDown = function(e) {
        // Only handle if this modal is visible
        if (!$trayPopup.hasClass('tray-popup-visible')) {
            return;
        }

        if (e.key === 'Escape' || e.keyCode === 27 || e.which === 27) {
            // Don't close tray if a Bootstrap modal is currently open inside it
            const $openBootstrapModal = $('.modal.show');
            if ($openBootstrapModal.length > 0) {
                // Let Bootstrap handle the ESC key for its modal
                return;
            }

            // Check if we're in disconnect modal view
            const $disconnectModal = $trayPopup.find('.ibc-consultant-disconnect-modal-content');
            if ($disconnectModal.length > 0 && !$disconnectModal.hasClass('d-none')) {
                // Trigger the cancel button to go back to card list
                e.preventDefault();
                const $disconnectTrigger = $trayPopup.find('.js-disconnect-cancel-btn');
                if ($disconnectTrigger.length > 0) {
                    $disconnectTrigger.first().trigger('click');
                }
                return;
            }


            e.preventDefault();
            closePopup($trayPopup);
            return;
        }

        if (e.key === 'Tab' || e.keyCode === 9) {
            const $focusableElements = $trayPopup.find(focusableSelector).filter(':visible').not('[tabindex="-1"]');

            if ($focusableElements.length === 0) {
                return;
            }

            const firstElement = $focusableElements[0];
            const lastElement = $focusableElements[$focusableElements.length - 1];
            // Shift + Tab (backwards)
            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else if (document.activeElement === lastElement) { // Tab (forwards)
                e.preventDefault();
                firstElement.focus();
            }
        }
    };

    $trayPopup.data('focusTrapHandler', handleKeyDown);
    $(document).on('keydown', handleKeyDown);
}

/**
* Initializes accessibility features for the modal after content is loaded.
*
 * @param {JQuery<HTMLElement>} $trayPopup - The tray modal DOM element.
 * @param {HTMLElement} triggerElement - The element that triggered the tray open.
*/
function initializeAccessibility($trayPopup, triggerElement) {
    const focusableSelector = 'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
    const previousFocusElement = triggerElement || document.activeElement;

    // Always store the current active element (the trigger) for focus restoration
    $trayPopup.data('previousFocus', previousFocusElement);

    // Set focus to the first focusable element (close button) after modal is visible
    setTimeout(() => {
        const $closeButton = $trayPopup.find('.tray-popup-close').first();
        const $focusableElements = $trayPopup.find(focusableSelector).filter(':visible');

        if ($focusableElements.length > 0) {
            const firstElement = $focusableElements[0];
            firstElement.focus();
        } else if ($closeButton.length > 0) {
            $closeButton[0].focus();
        }
    }, 300);

    // Remove any existing focus trap to prevent duplicate handlers
    removeFocusTrap($trayPopup);

    // Add focus trap
    setupFocusTrap($trayPopup);
}

/**
* Binds click events to close the tray modal.
*
* @param {JQuery<HTMLElement>} $trayPopup - The tray modal DOM element.
*/
function attachPopupCloseEvents($trayPopup) {
    const trayId = $trayPopup.attr('id').replace('tray-modal-', '');

    $trayPopup.off('click.trayClose', '.tray-popup-close').on('click.trayClose', '.tray-popup-close', e => {
        e.preventDefault();

        // Keep propagation for IBC filter "Show Results" so body-level filter handlers still run.
        if (!$(e.currentTarget).hasClass('show-results-btn')) {
            e.stopPropagation();
        }

        const $currentTrayPopup = $(e.currentTarget).closest('.tray-modal');
        closePopup($currentTrayPopup.length ? $currentTrayPopup : $trayPopup);
    });

    $('body').off('click.trayClose touchstart.trayClose', `.tray-modal-backdrop-${trayId}`).on('click.trayClose touchstart.trayClose', `.tray-modal-backdrop-${trayId}`, (e) => {
        e.stopPropagation();
        closePopup($trayPopup);
    });

    if ($trayPopup.find('[data-tray-modal="modal"][data-ajax-content]').length > 0 ) {
        initTray(); // eslint-disable-line
    }
}

/**
* Handles tray modal logic for both AJAX and static content.
*
* @param {Event} e - Click event
*/
function showTrayPopUp(e) {
    e.preventDefault();
    e.stopPropagation();

    const link = e.currentTarget;
    const contentUrl = link.dataset.ajaxContent;
    const $card = $(link).closest('.ibc-card-container');
    const customClassName = link.dataset.classnames;
    const isAjax = contentUrl !== 'false';
    const slotNumber = $card.index() + 1; // +1 so first card = slot 1
    const trayId = link.dataset.trayId || 'default'; // new attribute
    var ariaLabel = link.getAttribute('aria-label') || link.getAttribute('title') || '';

    const $trayPopup = getOrCreateTrayPopup(trayId, customClassName, ariaLabel);
    const $trayModalContent = $trayPopup.find('.tray-modal-content');
    $trayModalContent.empty();

    if (isAjax) {
        $.ajax({
            url: contentUrl,
            method: 'GET',
            success: function (data) {
                let contentHtml;
                let htmlRegex = /<[^>]+>/;
                if (!(htmlRegex.test(data)) && !data.success) {
                    window.location.reload();
                    return;
                }
                contentHtml = htmlRegex.test(data) ? data : data.renderedTemplate;
                $trayModalContent.empty().append(contentHtml);
                setTrayAccessibleName($trayPopup, link);
                showPopupWithDelay($trayPopup);
                attachPopupCloseEvents($trayPopup);
                setTriggerExpandedState(link, true);
                initializeAccessibility($trayPopup, link);

                $('body').trigger('initFloatLabels');
                formFields.updateSelect();

                // Trigger custom event with customClassName
                $('body').trigger('trayModalOpened', { customClassName });
                if (customClassName && customClassName.includes('search-ibc-profile-tray')) {
                    gtm.viewProfileSelect(data, slotNumber);
                }
                if (customClassName && customClassName.includes("mini-cart-tray")) {
                    gtm.openMinicart();
                }
            },
            error: function (error) {
                console.error('Tray Modal AJAX failure :(', error); // eslint-disable-line no-console
            }
        });
    } else {
        const $defaultContent = $(link).closest('.default-tray').find('.default-tray-content');
        if ($defaultContent.length) {
            $trayModalContent.empty().append($defaultContent.html());
            setTrayAccessibleName($trayPopup, link);
            showPopupWithDelay($trayPopup);
            attachPopupCloseEvents($trayPopup);
            setTriggerExpandedState(link, true);
            initializeAccessibility($trayPopup, link);

            $('body').trigger('ibcSearchFilterIdUpdate');
            $('body').trigger('ibcSearchFilterRetainValues');

            // Trigger custom event with customClassName
            $('body').trigger('trayModalOpened', { customClassName });
        }
    }

    $('button[data-toggle="tooltip"]').tooltip();
}

/**
* to initialize the tray
*/
function initTray() {
    $('[data-tray-modal="modal"][data-ajax-content]').each(function () {
        const $trigger = $(this);
        const tagName = ($trigger.prop('tagName') || '').toLowerCase();
        const isNativeInteractive = tagName === 'a' || tagName === 'button' || tagName === 'input';
        const hasPopupType = $trigger.attr('aria-haspopup');

        if (!hasPopupType || hasPopupType === 'true') {
            $trigger.attr('aria-haspopup', 'dialog');
        }

        if (!isNativeInteractive) {
            if (!$trigger.attr('role')) {
                $trigger.attr('role', 'button');
            }

            if (!$trigger.attr('tabindex')) {
                $trigger.attr('tabindex', '0');
            }
        }


        $trigger.off('click.tray').on('click.tray', e => showTrayPopUp(e));

        if (!isNativeInteractive) {
            $trigger.off('keydown.tray').on('keydown.tray', function (e) {
                if (e.which === 13 || e.which === 32) {
                    e.preventDefault();
                    showTrayPopUp(e);
                }
            });
        } else {
            $trigger.off('keydown.tray');
        }
    });
}

module.exports = {
    InitTrayModal: function () {
        initTray();

        const params = new URLSearchParams(window.location.search);
        if (params.get('open') === 'emailsignup') {
            const $emailSignupLink = $('[data-ajax-content*="EmailSignup-Show"]');

            if ($emailSignupLink.length > 0) {
                $emailSignupLink.trigger('click');
            }
        }

        $('body').on('tray:adjustHeight', function (e, $trayPopup) {
            const trayHeaderHeight = $trayPopup.find('.tray-header').outerHeight(true) || 0;
            var trayFooterHeight = $trayPopup.find('.tray-footer').outerHeight(true) || 0;
            if ($trayPopup.hasClass('email-signup-tray')) {
                trayFooterHeight = $trayPopup.find('.email-signup-tray__footer').outerHeight(true) || 0;
            }
            const trayHeaderFooterTotal = trayHeaderHeight + trayFooterHeight;
            document.documentElement.style.setProperty('--tray-offset-height', `${trayHeaderFooterTotal}px`);
            $trayPopup.find('.ibc-user-contact').css('height',  `calc(100dvh - ${trayHeaderFooterTotal}px)`);
        });
    }
};
